import { PdfTemplateSchema } from '@terra/shared-schemas-orchestrator';
import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  CreatePdfTemplateSchema,
  UpdatePdfTemplateSchema,
  PdfTemplateIdSchema,
  GeneratePdfRequestSchema,
  GeneratePdfResponseSchema,
  PdfJobSchema,
  DownloadFindingsPdfRequestSchema,
} from './pdf-generator.schema';

export const PdfGeneratorContract = terraContract.router({
  listTemplates: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_FINDINGS.name],
    })
    .route({
      method: 'GET',
      path: '/templates',
      summary: 'List PDF templates',
      description:
        'Retrieves all PDF templates available for the tenant, including default templates.',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        query: z.object({
          includeDefault: z.coerce.boolean().optional().prefault(true),
        }),
      }),
    )
    .output(
      z.object({
        templates: z.array(PdfTemplateSchema),
      }),
    ),
  getTemplate: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_FINDINGS.name],
    })
    .route({
      method: 'GET',
      path: '/templates/{templateId}',
      summary: 'Get PDF template',
      description: 'Retrieves a specific PDF template by ID.',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: PdfTemplateIdSchema }))
    .output(
      z.object({
        template: PdfTemplateSchema.nullable(),
      }),
    ),
  createTemplate: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
    })
    .route({
      method: 'POST',
      path: '/templates',
      summary: 'Create PDF template',
      description:
        'Creates a new PDF template with branding options and section configuration.',
      inputStructure: 'detailed',
      successStatus: 201,
    })
    .input(z.object({ body: CreatePdfTemplateSchema }))
    .output(
      z.object({
        template: PdfTemplateSchema,
      }),
    ),
  updateTemplate: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
    })
    .route({
      method: 'PATCH',
      path: '/templates/{templateId}',
      summary: 'Update PDF template',
      description: 'Updates an existing PDF template.',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: PdfTemplateIdSchema,
        body: UpdatePdfTemplateSchema.omit({ templateId: true }),
      }),
    )
    .output(
      z.object({
        template: PdfTemplateSchema,
      }),
    ),
  deleteTemplate: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
    })
    .route({
      method: 'DELETE',
      path: '/templates/{templateId}',
      summary: 'Delete PDF template',
      description:
        'Deletes a PDF template. Default templates cannot be deleted.',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: PdfTemplateIdSchema }))
    .output(
      z.object({
        success: z.boolean(),
      }),
    ),
  generatePdf: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
    })
    .route({
      method: 'POST',
      path: '/generate',
      summary: 'Generate PDF report',
      description:
        'Initiates async PDF generation. Returns a job ID for tracking progress via SSE.',
      inputStructure: 'detailed',
      successStatus: 202,
    })
    .input(z.object({ body: GeneratePdfRequestSchema }))
    .output(GeneratePdfResponseSchema),
  getJobStatus: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_FINDINGS.name],
    })
    .route({
      method: 'GET',
      path: '/jobs/{jobId}',
      summary: 'Get job status',
      description: 'Retrieves the current status of a PDF generation job.',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({
          jobId: z.string(),
        }),
      }),
    )
    .output(PdfJobSchema)
    .errors({
      NOT_FOUND: {
        status: 404,
        data: z.object({
          error: z.string(),
        }),
      },
    }),
  downloadFindingsPdf: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_FINDINGS.name],
    })
    .route({
      method: 'POST',
      path: '/download/findings',
      summary: 'Download findings PDF',
      description:
        'Generates and downloads a findings-only PDF directly. No storage, streams the PDF back immediately.',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: DownloadFindingsPdfRequestSchema }))
    .output(z.any())
    .errors({
      BAD_REQUEST: {
        status: 400,
        data: z.object({
          error: z.string(),
        }),
      },
      INTERNAL_SERVER_ERROR: {
        status: 500,
        data: z.object({
          error: z.string(),
        }),
      },
    }),
});
